import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import api from "../../services/products";
import { flattenObject } from "../../util/flattenObject";

const initialState = {
  filterLoading: true,
  productLoading: true,
  filterId: 0,
  products: [],
  productsCount: 0,
  pagination: {
    links: [],
    page: 1,
  },
  filter: {
    groups: [],
    options: [],
    powers: [],
    ranges: [],
  },
  request: {
    page: 1,
    maingroup: 0,
    groups: [],
    class: null,
    frequency: [],
    range: {},
    options: {},
    search: "",
    limit: 12,
  },
};

export const getFilter = createAsyncThunk(
  "filter/getFilter",
  async (id, thunkAPI) => {
    const request = { ...thunkAPI.getState().filter.request };

    // fix for direct provided url
    delete request.options;

    const data = await api.getFilter(request);

    return data.filter;
  }
);

export const getProducts = createAsyncThunk(
  "filter/getProducts",
  async (id, thunkAPI) => {
    const request = thunkAPI.getState().filter.request;
    const data = await api.getProducts(request);
    return data;
  }
);

export const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    setActiveFilterId: (state, action) => {
      state.request.maingroup = action.payload;
    },
    setGroups: (state, action) => {
      state.request.page = 1;
      state.request.range = {};
      state.request.options = {};
      // state.request.frequency = [];
      state.request.groups = [action.payload];
    },
    paginate: (state, action) => {
      state.request.page = action.payload;
      state.pagination.page = action.payload;
    },
    clearAllOptions: (state, action) => {
      state.request.options = {};
      state.request.groups = [];
      state.request.search = "";
    },
    setSearch: (state, action) => {
      state.request.page = 1;
      state.request.search = action.payload.trim();
    },
    setRequest: (state, action) => {
      state.request.page = 1;
      state.request = {
        ...initialState.request,
        ...action.payload,
      };
      if (
        !action.payload.range ||
        Object.keys(action.payload.range).length === 0
      ) {
        state.request.range = {};
      }
    },
    changePower: (state, action) => {
      state.request.page = 1;
      const { frequency, class: powerClass } = action.payload;
      state.request.frequency = frequency;
      state.request.class = powerClass;
      let data = {};
      state.filter.ranges.forEach((range) => {
        data[range.option] = {
          unit: range.classes[powerClass].units[0].unit,
        };
      });
      state.request.range = data;
    },
    updateUnitFromTo: (state, action) => {
      state.request.page = 1;
      const range = current(state.request.range);
      const option = action.payload.option;
      const oldState = range[option] ?? {};
      const newState = {
        ...oldState,
        from: action.payload.from,
        to: action.payload.to,
      };
      state.request.range = {
        ...state.request.range,
        [option]: newState,
      };
    },
    updateUnit: (state, action) => {
      state.request.page = 1;
      const range = current(state.request.range);
      const option = action.payload.option;
      const oldState = range[option] ?? {};
      const newState = {
        ...oldState,
        unit: action.payload.unit,
        from: action.payload.from,
        to: action.payload.to,
      };
      state.request.range = {
        ...state.request.range,
        [option]: newState,
      };
    },
    setOptions: (state, action) => {
      state.request.page = 1;
      let options = JSON.parse(JSON.stringify(current(state.request.options)));
      const { option, value } = action.payload;
      let values = options[option] ?? [];
      if (values.includes(value)) {
        values = values.filter((el) => el !== value);
      } else {
        values.push(value);
      }
      options = {
        ...options,
        [option]: values,
      };
      state.request.options = options;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFilter.pending, (state) => {
        state.filterLoading = true;
      })
      .addCase(getFilter.fulfilled, (state, action) => {
        state.filter = action.payload;
        if (Object.keys(state.request.range).length === 0) {
          let data = {};
          state.filter.ranges.forEach((range) => {
            data[range.option] = {
              unit: range.classes[state.request.class].units[0].unit,
            };
          });
          state.request.range = data;
        }
        state.filterLoading = false;
      })
      .addCase(getFilter.rejected, (state) => {
        state.filterLoading = false;
      })
      .addCase(getProducts.pending, (state) => {
        state.productLoading = true;
        const searchParams = new URLSearchParams(flattenObject(state.request));
        window.history.pushState(
          {},
          "",
          window.location.origin +
            window.location.pathname +
            "?" +
            searchParams.toString()
        );
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.products = action.payload.products;
        state.productsCount = action.payload.products_count;
        state.productLoading = false;

        const limit = state.request.limit;
        const correctPageCount = Math.ceil(
          action.payload.products_count / limit
        );
        const pagination = action.payload.pagination || {
          page: 1,
          links: [],
          first: "",
          prev: "",
          next: "",
          last: "",
        };
        const currentPage = parseInt(pagination.page);
        const links = Array.isArray(pagination.links) ? pagination.links : [];

        state.pagination = {
          ...pagination,
          links: links.slice(0, correctPageCount),
          next:
            currentPage < correctPageCount
              ? pagination.next
              : "",
          prev: currentPage > 1 ? action.payload.pagination.prev : "",
        };
      });
  },
});

export const {
  setFilter,
  setOptions,
  setGroups,
  setActiveFilterId,
  setRequest,
  changePower,
  updateUnit,
  updateUnitFromTo,
  paginate,
  clearAllOptions,
  setSearch,
} = filterSlice.actions;

export default filterSlice.reducer;
